"use client";
import * as React from "react";
import { useState, useEffect } from "react";

type FormState = Pick<
  UnSavedDirectory,
  | "name"
  | "log_webhook_events"
  | "webhook_url"
  | "webhook_secret"
  | "google_domain"
  | "google_authorization_url"
>;
import type { Directory, EditDirectoryProps, UnSavedDirectory } from "../types";
import ToggleConnectionStatus from "../ToggleConnectionStatus/index";
import defaultClasses from "./index.module.css";
import cssClassAssembler from "../../sso/utils/cssClassAssembler";
import Button from "../../shared/Button/index";
import Spacer from "../../shared/Spacer/index";
import ConfirmationPrompt from "../../shared/ConfirmationPrompt/index";
import Checkbox from "../../shared/Checkbox/index";
import InputField from "../../shared/inputs/InputField/index";
import SecretInputFormControl from "../../shared/inputs/SecretInputFormControl/index";
import { InputWithCopyButton } from "../../shared";
import LoadingContainer from "../../shared/LoadingContainer/index";
import { sendHTTPRequest } from "../../shared/http";
const DEFAULT_FORM_STATE: FormState = {
  name: "",
  log_webhook_events: false,
  webhook_url: "",
  webhook_secret: "",
  google_domain: "",
  google_authorization_url: "",
};

function EditDirectory(props: EditDirectoryProps) {
  const [showDelConfirmation, setShowDelConfirmation] = useState(() => false);

  function toggleDelConfirmation() {
    setShowDelConfirmation(!showDelConfirmation);
  }

  const [isSaving, setIsSaving] = useState(() => false);

  const [isDirectoryLoading, setIsDirectoryLoading] = useState(() => true);

  const [directoryUpdated, setDirectoryUpdated] = useState(
    () => DEFAULT_FORM_STATE
  );

  function classes() {
    return {
      inputField: {
        label: props.classNames?.label,
        input: props.classNames?.input,
        container: props.classNames?.fieldContainer,
      },
      section: cssClassAssembler(
        props.classNames?.section,
        defaultClasses.section
      ),
    };
  }

  function updateFormState(key: string, newValue: string | boolean) {
    return {
      ...directoryUpdated,
      [key]: newValue,
    };
  }

  function handleChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const id = target.id;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setDirectoryUpdated(updateFormState(id, value));
  }

  function onSubmit(event: Event) {
    event.preventDefault();
    setIsSaving(true);
    async function saveDirectory(url: string) {
      const response = await sendHTTPRequest<{
        data: Directory;
      }>(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(directoryUpdated),
      });
      setIsSaving(false);
      if (response && typeof response === "object") {
        if ("error" in response && response.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(response.error.message);
        } else if ("data" in response && response.data) {
          typeof props.successCallback === "function" &&
            props.successCallback({
              operation: "UPDATE",
              connection: response.data,
            });
        }
      }
    }
    saveDirectory(props.urls.patch);
  }

  function deleteDirectory() {
    async function deleteConnection(url: string) {
      const response = await sendHTTPRequest<null>(url, {
        method: "DELETE",
      });
      if (response && typeof response === "object") {
        if (response.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(response.error.message);
        } else {
          typeof props.successCallback === "function" &&
            props.successCallback({
              operation: "DELETE",
            });
        }
      }
    }
    deleteConnection(props.urls.delete);
  }

  function isExcluded(
    fieldName: Exclude<EditDirectoryProps["excludeFields"], undefined>[number]
  ) {
    return !!props.excludeFields?.includes(fieldName);
  }

  function shouldDisplayHeader() {
    if (props.displayHeader !== undefined) {
      return props.displayHeader;
    }
    return true;
  }

  function directoryFetchUrl() {
    return props.urls.get;
  }

  function googleSCIMAuthzURL() {
    if (!directoryUpdated.google_authorization_url) {
      return undefined;
    }
    let _url = directoryUpdated.google_authorization_url;
    const [urlPath, qs] = _url.split("?");
    const urlParams = new URLSearchParams(qs);
    urlParams.set("directoryId", directoryUpdated.id);
    return `${urlPath}?${urlParams}`;
  }

  useEffect(() => {
    async function getDirectory(url: string) {
      const response = await sendHTTPRequest<{
        data: Directory;
      }>(url);
      setIsDirectoryLoading(false);
      if (response && typeof response === "object") {
        if ("error" in response && response.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(response.error.message);
        } else if ("data" in response) {
          const directoryData = response.data;
          setDirectoryUpdated({
            ...directoryData,
            name: directoryData.name,
            log_webhook_events: directoryData.log_webhook_events,
            webhook_url: directoryData.webhook?.endpoint || "",
            webhook_secret: directoryData.webhook?.secret || "",
            google_authorization_url:
              directoryData.google_authorization_url || "",
            google_domain: directoryData.google_domain || "",
            deactivated: directoryData.deactivated,
          });
        }
      }
    }
    getDirectory(directoryFetchUrl());
  }, [directoryFetchUrl()]);

  return (
    <LoadingContainer isBusy={isDirectoryLoading}>
      <div className={defaultClasses.headingContainer}>
        {shouldDisplayHeader() ? (
          <h2 className={defaultClasses.heading}>Update Directory</h2>
        ) : null}
        <ToggleConnectionStatus
          connection={directoryUpdated}
          urls={{
            patch: props.urls.patch,
          }}
          classNames={{
            confirmationPrompt: {
              button: {
                ctoa: `${props.classNames?.confirmationPrompt?.button?.ctoa} ${
                  directoryUpdated?.deactivated
                    ? props.classNames?.button?.ctoa
                    : props.classNames?.button?.destructive
                }`.trim(),
                cancel: props.classNames?.confirmationPrompt?.button?.cancel,
              },
            },
          }}
          errorCallback={props.errorCallback}
          successCallback={props.successCallback}
        />
      </div>
      <form onSubmit={(event) => onSubmit(event)}>
        {!isExcluded("name") ? (
          <>
            <InputField
              label="Directory name"
              id="name"
              value={directoryUpdated.name}
              handleInputChange={handleChange}
              required
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("scim_endpoint") && directoryUpdated?.type !== "google" ? (
          <>
            <InputWithCopyButton
              label="SCIM Endpoint"
              text={directoryUpdated.scim?.endpoint}
              copyDoneCallback={props.successCallback}
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("scim_token") && directoryUpdated?.type !== "google" ? (
          <>
            <InputWithCopyButton
              label="SCIM Token"
              text={directoryUpdated.scim?.secret}
              copyDoneCallback={props.successCallback}
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("google_domain") && directoryUpdated?.type === "google" ? (
          <>
            <InputField
              label="Directory domain"
              id="google_domain"
              value={directoryUpdated.google_domain}
              handleInputChange={handleChange}
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("google_authorization_url") &&
        directoryUpdated?.type === "google" &&
        googleSCIMAuthzURL() ? (
          <>
            <InputWithCopyButton
              label="Google SCIM Authorization url"
              text={googleSCIMAuthzURL()}
              copyDoneCallback={props.successCallback}
              classNames={classes().inputField}
            />
            <div id="scim-authz-hint" className={defaultClasses.hint}>
              The URL that your tenant needs to authorize the application to
              access their Google Directory.
            </div>
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("webhook_url") ? (
          <>
            <InputField
              type="url"
              label="Webhook URL"
              id="webhook_url"
              value={directoryUpdated.webhook_url}
              handleInputChange={handleChange}
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("webhook_secret") ? (
          <>
            <SecretInputFormControl
              label="Webhook secret"
              id="webhook_secret"
              classNames={classes().inputField}
              handleChange={handleChange}
              value={directoryUpdated.webhook_secret}
              copyDoneCallback={props.successCallback}
              required={false}
              readOnly={false}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("log_webhook_events") ? (
          <>
            <Checkbox
              label="Enable Webhook events logging"
              id="log_webhook_events"
              name="log_webhook_events"
              checked={directoryUpdated?.log_webhook_events}
              handleChange={handleChange}
            />
            <Spacer y={6} />
          </>
        ) : null}
        <div className={defaultClasses.formAction}>
          {typeof props.cancelCallback === "function" ? (
            <Button
              type="button"
              name="Cancel"
              variant="outline"
              handleClick={props.cancelCallback}
              classNames={props.classNames?.button?.cancel}
            />
          ) : null}
          {!props.hideSave ? (
            <Button
              type="submit"
              name="Save"
              variant="primary"
              classNames={props.classNames?.button?.ctoa}
              isLoading={isSaving}
            />
          ) : null}
        </div>
      </form>
      <section className={classes().section}>
        <div className={defaultClasses.info}>
          <h6 className={defaultClasses.sectionHeading}>
            Delete this directory connection
          </h6>
          <p className={defaultClasses.sectionPara}>
            All your apps using this connection will stop working.
          </p>
        </div>
        {!showDelConfirmation ? (
          <Button
            name="Delete"
            variant="destructive"
            type="button"
            handleClick={toggleDelConfirmation}
            classNames={props.classNames?.button?.destructive}
          />
        ) : null}
        {showDelConfirmation ? (
          <ConfirmationPrompt
            ctoaVariant="destructive"
            promptMessage=" Are you sure you want to delete the directory connection? This will permanently delete the             directory connection, users, and groups."
            classNames={{
              button: {
                ctoa: `${props.classNames?.button?.destructive} ${props.classNames?.confirmationPrompt?.button?.ctoa}`.trim(),
                cancel: props.classNames?.confirmationPrompt?.button?.cancel,
              },
            }}
            cancelCallback={toggleDelConfirmation}
            confirmationCallback={deleteDirectory}
          />
        ) : null}
      </section>
    </LoadingContainer>
  );
}

export default EditDirectory;
